/**
 * speech.js — Speech-to-text service
 *
 * Uses the browser's native Web Speech API (SpeechRecognition).
 * No external SDK or API key required. When Azure credentials
 * arrive, swap the implementation here without touching callers.
 *
 * USAGE:
 *   if (speech.isAvailable()) {
 *     speech.start({ onResult, onEnd, onError });
 *     speech.stop();
 *   }
 *
 * onResult(transcript, isFinal)
 * onEnd()
 * onError(err)
 */

function getCtor() {
  return (
    (typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
    null
  );
}

export const speech = {
  _recognition: null,
  _listening: false,

  isAvailable() {
    return !!getCtor();
  },

  isListening() {
    return this._listening;
  },

  start({ onResult, onEnd, onError, lang = "en-US", interim = true } = {}) {
    const Ctor = getCtor();
    if (!Ctor) {
      onError && onError(new Error("Speech recognition not supported"));
      return false;
    }

    if (this._listening) return true;

    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = interim;

    rec.onresult = (e) => {
      let finalText = "";
      let interimText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interimText += r[0].transcript;
      }
      const transcript = (finalText || interimText).trim();
      if (!transcript) return;
      onResult && onResult(transcript, !!finalText);
    };

    rec.onerror = (e) => {
      onError && onError(e.error || e);
    };

    rec.onend = () => {
      this._listening = false;
      this._recognition = null;
      onEnd && onEnd();
    };

    try {
      rec.start();
    } catch (err) {
      onError && onError(err);
      return false;
    }

    this._recognition = rec;
    this._listening = true;
    return true;
  },

  stop() {
    if (!this._recognition) return;
    try {
      this._recognition.stop();
    } catch {
      /* ignore */
    }
  },
};
